export const DOCUMENT_MENU_ROLES = ['oms-admin', 'oms-customer-service', 'oms-shop-manager', 'default-user']
export const STANDARD_DOCUMENT_COLUMNS = ['type', 'name', 'created_at']
export const DOCUMENT_ENTITY_TYPES = ['orders', 'subOrders', 'returns']
// Reserved entity_type value the api reads as "attached to no entity". It is a filter criterion, not
// an entity type: it stays out of DOCUMENT_ENTITY_TYPES, and is only ever shown through its label.
export const DOCUMENT_NO_ENTITY = '__NONE__'
// Values the entity type filter offers, in display order: the list view selector and the default
// filter of the display configuration propose the same choices, "None" included.
export const DOCUMENT_ENTITY_FILTER_VALUES = [...DOCUMENT_ENTITY_TYPES, DOCUMENT_NO_ENTITY]
// The list and its configuration must name an entity the same way, so the wording lives in one place.
export const DOCUMENT_ENTITY_LABELS = {
  orders: 'documents.orderEntity',
  subOrders: 'documents.subOrderEntity',
  returns: 'documents.returnEntity',
  [DOCUMENT_NO_ENTITY]: 'documents.noEntity',
}

/**
 * Build defaults for a tenant without display configuration.
 * @returns {Object} Default roles, columns, filters and available fields.
 */
export function defaultDocumentDisplay() {
  return {
    menu_roles: [],
    list: { columns: ['type', 'name'], default_filters: { document_types: [], entity_types: [] } },
    available_fields: STANDARD_DOCUMENT_COLUMNS.map((key) => ({ key })),
  }
}

/**
 * Copy editable display settings without registry or response metadata.
 * @param {Object} settings Display configuration from the editor or API.
 * @returns {Object} Independent payload containing menu roles and list settings.
 */
export function documentDisplayPayload(settings) {
  return {
    menu_roles: [...settings.menu_roles],
    list: {
      columns: [...settings.list.columns],
      default_filters: {
        document_types: [...settings.list.default_filters.document_types],
        entity_types: [...settings.list.default_filters.entity_types],
      },
    },
  }
}

/**
 * Validate the mandatory document column selection.
 * @param {string[]} columns Ordered column keys.
 * @returns {string} Translation key for the error, or an empty string.
 */
export function documentColumnsError(columns) {
  if (!columns.length) {
    return 'emptyColumns'
  }
  if (!columns.includes('name')) {
    return 'nameRequired'
  }
  return ''
}

/**
 * Check configured menu access, allowing Super Admin independently.
 * @param {Object|null} settings Tenant display configuration.
 * @param {string[]} roles Current user roles.
 * @param {boolean} [superAdmin=false] Whether the user is Super Admin.
 * @returns {boolean} Whether the Documents menu is accessible.
 */
export function documentMenuAllowed(settings, roles, superAdmin = false) {
  return superAdmin || (Array.isArray(roles) && roles.some((role) => settings?.menu_roles?.includes(role)))
}

/**
 * Build the list view filters from the tenant's configured defaults: the initial state of the list
 * and the target of the filter dialog "Reset". An empty configured list means no criterion, so the
 * filter starts inactive. The same defaults apply in store view, which lists every document of the
 * store whatever its attachment.
 * @param {Object} display - Document display settings as returned by the API.
 * @returns {Array<Object>} `{ name, value, active }` filters, each value a fresh array.
 */
export function documentDefaultFilters(display) {
  const defaults = display?.list?.default_filters || {}
  const criteria = [
    { name: 'type', value: defaults.document_types },
    { name: 'entity_type', value: defaults.entity_types },
  ]
  return criteria.map(({ name, value }) => {
    const values = Array.isArray(value) ? [...value] : []
    return { name, value: values, active: values.length > 0 }
  })
}

/**
 * Resolve the list view column keys, in tenant order, keeping only keys still present in the
 * display dictionary (a deactivated custom field disappears from `available_fields` and must
 * disappear from the list view even if it is still listed in `list.columns`).
 * @param {Object} display - Document display settings as returned by the API.
 * @returns {Array<string>} Ordered column keys to render in the list view.
 */
export function documentColumnKeys(display) {
  if (!Array.isArray(display?.list?.columns) || !Array.isArray(display?.available_fields)) {
    return []
  }
  const availableKeys = new Set(display.available_fields.map((field) => field.key))
  return display.list.columns.filter((key) => availableKeys.has(key))
}

/**
 * Resolve the active custom fields of the tenant display dictionary, in registry order, excluding
 * the standard keys the front translates itself. `available_fields` holds every field a document may
 * carry and is the same for every role, so this covers the keys a document carries without being a
 * selected column — what the values modal resolves. Presence means active.
 * @param {Object} display - Document display settings as returned by the API.
 * @returns {Array<Object>} Custom field definitions from `available_fields`.
 */
export function documentActiveCustomFields(display) {
  if (!Array.isArray(display?.available_fields)) {
    return []
  }
  return display.available_fields.filter((field) => !STANDARD_DOCUMENT_COLUMNS.includes(field.key))
}

/**
 * Resolve the display label of a custom field for a given locale, falling back to English then to
 * the raw key.
 * @param {Object} field - Custom field definition (with an optional `labels` map).
 * @param {string} locale - Active back-office locale.
 * @returns {string} The label to display.
 */
export function documentCustomFieldLabel(field, locale) {
  return field?.labels?.[locale] || field?.labels?.en || field?.key
}

/**
 * Index a document's custom field values by field name for lookup by column key.
 * Convention (not enforced by the API): a custom column's `key` matches a custom field's `name`.
 * @param {Object} document - A document as returned by the API.
 * @returns {Map<string, Object>} Custom field entries keyed by `name`.
 */
export function documentCustomValues(document) {
  const values = new Map()
  if (!Array.isArray(document?.custom_fields)) {
    return values
  }
  // The registry guarantees unique field names, so a duplicate `name` is a data anomaly.
  // Keep the first occurrence so storage order never silently changes what is displayed.
  for (const field of document.custom_fields) {
    if (!values.has(field.name)) {
      values.set(field.name, field)
    }
  }
  return values
}
